import React, { useState, useEffect } from 'react';
import { Table, Button, Modal, Form, Input, Typography, Space, message, Popconfirm, Select } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, SearchOutlined, TeamOutlined } from '@ant-design/icons';
import { Customer, CreateCustomerData, UpdateCustomerData, Warehouse } from '../types.ts';
import { customerAPI, warehouseAPI } from '../services/api.ts';
import { useAuth } from '../contexts/AuthContext.tsx';

const { Title } = Typography;
const { Search } = Input;
const { Option } = Select;

const Customers: React.FC = () => {
  const { canWrite } = useAuth();
  const canEdit = canWrite('customers');
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState<Customer | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [form] = Form.useForm();

  const fetchCustomers = async (search?: string) => {
    setLoading(true);
    try {
      const data = await customerAPI.getCustomers(search);
      setCustomers(data);
    } catch (error) {
      message.error('Errore nel caricamento clienti');
    } finally {
      setLoading(false);
    }
  };

  const fetchWarehouses = async () => {
    try {
      const data = await warehouseAPI.getWarehouses();
      setWarehouses(data);
    } catch (error) {
      message.error('Errore nel caricamento magazzini');
    }
  };

  useEffect(() => {
    fetchCustomers();
    fetchWarehouses();
  }, []);

  const handleAdd = () => {
    setEditingCustomer(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleEdit = (customer: Customer) => {
    setEditingCustomer(customer);
    form.setFieldsValue({
      company: customer.company,
      customer_code: customer.customer_code,
      vendor_code: customer.vendor_code,
      warehouse_id: customer.warehouse_id,
      description: customer.description,
      email: customer.email,
      contact_person: customer.contact_person,
      phone: customer.phone,
      tax_code: customer.tax_code,
      vat_number: customer.vat_number,
      address: customer.address,
      city: customer.city,
      locality: customer.locality,
      province: customer.province,
      postal_code: customer.postal_code,
      country: customer.country,
      notes: customer.notes,
    });
    setModalVisible(true);
  };

  const handleDelete = async (id: number) => {
    try {
      await customerAPI.deleteCustomer(id);
      message.success('Cliente eliminato con successo');
      fetchCustomers(searchTerm);
    } catch (error: any) {
      message.error(error.response?.data?.detail || 'Errore nell\'eliminazione del cliente');
    }
  };

  const handleSubmit = async (values: any) => {
    try {
      if (editingCustomer) {
        // Select azzerabile: undefined sparirebbe dal JSON e il server terrebbe il
        // vecchio magazzino; null = "nessun magazzino di riferimento"
        const updateData: UpdateCustomerData = { ...values, warehouse_id: values.warehouse_id ?? null };
        await customerAPI.updateCustomer(editingCustomer.id, updateData);
        message.success('Cliente aggiornato con successo');
      } else {
        const createData: CreateCustomerData = values;
        await customerAPI.createCustomer(createData);
        message.success('Cliente creato con successo');
      }
      setModalVisible(false);
      fetchCustomers(searchTerm);
    } catch (error: any) {
      const errorMessage = error.response?.data?.detail || 'Errore nell\'operazione';
      message.error(errorMessage);
    }
  };

  const handleSearch = (value: string) => {
    setSearchTerm(value);
    fetchCustomers(value);
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
    },
    {
      title: 'Azienda',
      dataIndex: 'company',
      key: 'company',
      render: (company: string) => <strong>{company}</strong>,
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      render: (email: string) => email || '-',
    },
    {
      title: 'Codice Cliente',
      dataIndex: 'customer_code',
      key: 'customer_code',
      render: (code: string) => code || '-',
    },
    {
      title: 'Vendor Code',
      dataIndex: 'vendor_code',
      key: 'vendor_code',
      render: (code: string) => code || '-',
    },
    {
      title: 'Magazzino di Riferimento',
      key: 'warehouse',
      render: (_, record: Customer) => {
        if (record.warehouse) {
          return record.warehouse.cod_mag 
            ? `[${record.warehouse.cod_mag}] ${record.warehouse.description}`
            : record.warehouse.description;
        }
        return '-';
      },
    },
    {
      title: 'Telefono',
      dataIndex: 'phone',
      key: 'phone',
      render: (phone: string) => phone || '-',
    },
    {
      title: 'Città',
      dataIndex: 'city',
      key: 'city',
      render: (city: string) => city || '-',
    },
    {
      title: 'Paese',
      dataIndex: 'country',
      key: 'country',
      render: (country: string) => country || '-',
    },
    {
      title: 'Data Creazione',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date: string) => new Date(date).toLocaleDateString('it-IT'),
    },
    {
      title: 'Azioni',
      key: 'actions',
      width: 120,
      render: (_, record: Customer) => (
        <Space>
          <Button
            type="primary"
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          />
          <Popconfirm
            title="Sei sicuro di voler eliminare questo cliente?"
            onConfirm={() => handleDelete(record.id)}
            okText="Sì"
            cancelText="No"
          >
            <Button
              type="primary"
              danger
              size="small"
              icon={<DeleteOutlined />}
            />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <Title level={2}>
          <TeamOutlined style={{ marginRight: 8 }} />
          Gestione Clienti
        </Title>
        {canEdit && (
          <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
            Nuovo Cliente
          </Button>
        )}
      </div>

      <div style={{ marginBottom: 16 }}>
        <Search
          placeholder="Cerca per azienda, email o telefono..."
          allowClear
          enterButton={<SearchOutlined />}
          size="large"
          onSearch={handleSearch}
          style={{ maxWidth: 400 }}
        />
      </div>

      <Table
        columns={canEdit ? columns : columns.filter((c) => c.key !== 'actions')}
        dataSource={customers}
        loading={loading}
        rowKey="id"
        pagination={{ pageSize: 10 }}
      />

      <Modal
        title={editingCustomer ? 'Modifica Cliente' : 'Nuovo Cliente'}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        onOk={() => form.submit()}
        destroyOnClose
        width={800}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          {/* Sezione Dati Principali */}
          <Form.Item
            name="company"
            label="Azienda"
            rules={[{ required: true, message: 'Nome azienda richiesto' }]}
          >
            <Input placeholder="Inserisci il nome dell'azienda" />
          </Form.Item>

          <Form.Item
            name="description"
            label="Descrizione"
          >
            <Input placeholder="Descrizione o ragione sociale estesa" />
          </Form.Item>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px' }}>
            <Form.Item
              name="customer_code"
              label="Codice Cliente"
            >
              <Input placeholder="CUST001" />
            </Form.Item>

            <Form.Item
              name="vendor_code"
              label="Vendor Code"
            >
              <Input placeholder="Codice fornitore presso il cliente" />
            </Form.Item>

            <Form.Item
              name="warehouse_id"
              label="Magazzino di Riferimento"
            >
              <Select
                placeholder="Seleziona magazzino"
                allowClear
                showSearch
                optionFilterProp="children"
              >
                {warehouses.map((warehouse) => (
                  <Option key={warehouse.id} value={warehouse.id}>
                    {warehouse.cod_mag ? `[${warehouse.cod_mag}] ` : ''}{warehouse.description}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </div>

          {/* Sezione Contatti */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            <Form.Item
              name="contact_person"
              label="Persona di Contatto"
            >
              <Input placeholder="Nome e cognome referente" />
            </Form.Item>

            <Form.Item
              name="phone"
              label="Telefono"
            >
              <Input placeholder="+39 123 456 7890" />
            </Form.Item>
          </div>

          <Form.Item
            name="email"
            label="Email"
            rules={[{ type: 'email', message: 'Email non valida' }]}
          >
            <Input placeholder="email@azienda.com" />
          </Form.Item>

          {/* Sezione Dati Fiscali */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            <Form.Item
              name="tax_code"
              label="Codice Fiscale"
            >
              <Input placeholder="RSSMRA80A01H501U" />
            </Form.Item>

            <Form.Item
              name="vat_number"
              label="Partita IVA"
            >
              <Input placeholder="IT12345678901" />
            </Form.Item>
          </div>

          {/* Sezione Indirizzo */}
          <Form.Item
            name="address"
            label="Indirizzo"
          >
            <Input placeholder="Via/Piazza, numero civico" />
          </Form.Item>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            <Form.Item
              name="city"
              label="Città"
            >
              <Input placeholder="Milano" />
            </Form.Item>

            <Form.Item
              name="locality"
              label="Località/Frazione"
            >
              <Input placeholder="Frazione o località" />
            </Form.Item>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px' }}>
            <Form.Item
              name="province"
              label="Provincia"
            >
              <Input placeholder="MI" maxLength={10} />
            </Form.Item>

            <Form.Item
              name="postal_code"
              label="CAP"
            >
              <Input placeholder="20100" />
            </Form.Item>

            <Form.Item
              name="country"
              label="Paese"
            >
              <Input placeholder="Italia" />
            </Form.Item>
          </div>

          {/* Sezione Note */}
          <Form.Item
            name="notes"
            label="Note"
          >
            <Input.TextArea rows={4} placeholder="Note aggiuntive sul cliente" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Customers;
